import clsx from "clsx";
import React, { useContext, useEffect, useRef, useState } from "react";
import Editor from "../../../Editor/Editor";
import { smetaModals, SmetaModalsButtonsContext } from "../../Tools/modalManager";
import CheckBoxWithLabel from "./Components/CheckBoxWithLabel";
import { getColumnsGenNums, getDefaultFieldsGenNums } from "./Components/simpleLoacalGrid/simpleLocalGridColumns";
import styles from "./Components/dialogStyles.module.css";
import DialogSimpleLocalTable from "./DialogSimpleLocalTable";
import { Prefixes } from "../../Tools/ProjectX";
import smetaAxios from "../../../SmetaAxios";

const getGroupParams = (data: { module: Prefixes; groupID: string }): Promise<Data> => {
  const params = {
    GroupID: data.groupID,
  };
  const config = {
    prefix: data.module,
    method: "GET",
    url: "GetGroupParams",
    params,
  };

  return smetaAxios(config);
};

const setGroupParams = (data: {
  module: Prefixes;
  groupID: string;
  code: string;
  format: string;
  name: string;
  preview: string;
}) => {
  const params = {
    GroupID: data.groupID,
    Code: data.code,
    Format: data.format,
    GenName: data.name,
    Preview: data.preview,
  };
  const config = {
    prefix: data.module,
    method: "GET",
    url: "SetGroupParams",
    params,
  };

  return smetaAxios(config);
};

interface Props {
  props: { Buttons: string; Module: Prefixes; GroupID: string; hiddenButton: boolean };
}

interface Data {
  Code: string;
  Format: string;
  Name: string;
  GenName: { items: Array<any> };
  Preview: string;
  Mask?: string;
}

interface Result {
  code: string;
  format: string;
  name: string;
  preview: string;
}

interface EditData {
  value: string;
}

interface CheckEditData {
  CheckState: string;
}

/**
 * Контент диалога параметры дерева
 *
 * Поля компонента:
 *    1. [data, setData] - управление состоянием данных
 *    2. [code, setCode] - управление состоянием значения кода
 *    3. [format, setFormat] - управление состоянием значения формата
 *    4. [name, setName] - управление состоянием значения названия
 *    5. [preview, setPreview] - управление состоянием значения превью
 *    6. result - объект с результатами редактирования
 *
 * @param {Object} [props] объект с данными для компонента подразделений
 * @param {string} [props.Buttons] настрока видимых кнопок
 * @param {Prefixes} [props.module] модуль объекта
 * @param {Object} [props.groupId] id ветки дерева
 * @param {string} [props.hiddenButton] скрытие кнопок
 *
 * @returns Возвращает контент диалога для параметров дерева
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export default function DialogTreeParams({ props }: Props) {
  const { Buttons: buttons, hiddenButton, Module: module, GroupID: groupId } = props;

  const [data, setData] = useState<Data>();
  const [code, setCode] = useState("");
  const [format, setFormat] = useState("");
  const [name, setName] = useState("");
  const [preview, setPreview] = useState("0");

  const result = useRef<Result>({
    code: "",
    format: "",
    name: "",
    preview: "0",
  });

  const setButtons = useContext(SmetaModalsButtonsContext);

  /**
   * Инициализация параметров по умолчанию
   */
  const loadData = async () => {
    setData(
      await getGroupParams({
        module: module,
        groupID: groupId,
      }),
    );
  };
  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (hiddenButton) return;
    const modalButtons: { [key: number]: { label: string; onClick?: () => void } } = {};
    if (!buttons || Number(buttons) & 1) {
      modalButtons[1] = { label: "Установить", onClick: onSelect };
    }
    modalButtons[2] = { label: "Закрыть" };
    setButtons?.(modalButtons);
  }, [setButtons, result]);

  /**
   * Обновление данных при изменении данных по умолчанию
   */
  const updateData = () => {
    setCode(data?.Code || "");
    setFormat(data?.Format || "");
    setName(data?.Name || "");
    setPreview(data?.Preview || "0");
    result.current = {
      code: data?.Code || "",
      format: data?.Format || "",
      name: data?.Name || "",
      preview: data?.Preview || "0",
    };
  };
  useEffect(updateData, [data]);

  /**
   * Редактирует значение кода
   * @param editData данные от редактора
   */
  const onEditCode = async (editData: EditData) => {
    setCode(editData.value);
    result.current.code = editData.value;
  };

  /**
   * Редактирует значение формата
   * @param editData данные от редактора
   */
  const onEditFormat = async (editData: EditData) => {
    setFormat(editData.value);
    result.current.format = editData.value;
  };

  /**
   * Редактирует значение названия
   * @param editData данные от редактора
   */
  const onEditName = async (editData: EditData) => {
    setName(editData.value);
    result.current.name = editData.value;
  };

  /**
   * Редактирует значение превью
   * @param editData данные от редактора
   */
  const onCheckPreview = async (editData: CheckEditData) => {
    setPreview(editData.CheckState);
    result.current.preview = editData.CheckState;
  };

  /**
   * Открывает диалог с таблицей
   */
  const onEditNameButtonClick = async () => {
    smetaModals.open(
      <DialogSimpleLocalTable
        props={{
          Buttons: 2,
          records: data?.GenName?.items || [],
          columns: getColumnsGenNums(),
          defaultFields: getDefaultFieldsGenNums(),
          keyField: "CODE",
          recordKeys: ["OrgTypeCls", "OrgName", "Value"],
          rights: "2",
          setData: changeData,
        }}
      />,
      { Buttons: 2, Title: "Параметры группы" },
    );
  };

  /**
   * Изменяет дефолтные данные
   * @param newRecords новые записи для таблицы
   */
  const changeData = (newRecords: Array<any>) => {
    if (data?.GenName) {
      data.GenName.items = newRecords;
      setData({ ...data });
    }
  };

  /**
   * Устанавливает параметры для группы дерева
   */
  const onSelect = async () => {
    setGroupParams({ module: module, groupID: groupId, ...result.current });
  };

  return (
    <div className={clsx(styles.mainDialogBlock)}>
      <div className={clsx(styles.fullDialogFlexContainer, styles.dialogTreeParamsMainBlock)}>
        <div className={clsx(styles.dialogTreeParamsTitle)}>Основные средства</div>
        <div className={clsx(styles.dialogTreeParamsItem)}>
          <div className={clsx(styles.dialogTreeParamsLabel)}>Код группы: </div>
          <div className={clsx(styles.dialogTreeParamsEditorDiv)}>
            <Editor onEdit={onEditCode} value={code} editMask={data?.Mask} />
          </div>
        </div>
        <div className={clsx(styles.dialogTreeParamsItem)}>
          <div className={clsx(styles.dialogTreeParamsLabel)}>Формат инв. номера: </div>
          <div className={clsx(styles.dialogTreeParamsEditorDiv)}>
            <Editor onEdit={onEditFormat} value={format} />
          </div>
        </div>
        <div className={clsx(styles.dialogTreeParamsItem)}>
          <div className={clsx(styles.dialogTreeParamsLabel)}>Генератор номера: </div>
          <div className={clsx(styles.dialogTreeParamsEditorDiv)}>
            <Editor onEditButtonClick={onEditNameButtonClick} onEdit={onEditName} value={name} editStyle={16} />
          </div>
        </div>
        <div className={clsx(styles.dialogTreeParamsItem)}>
          <CheckBoxWithLabel
            onEdit={onCheckPreview}
            label="Использовать для представления объектов из других групп"
            CheckState={preview}
          />
        </div>
      </div>
    </div>
  );
}
